/**
 * @file confirmWithBank.ts
 * @brief Run the bank's 3D Secure step for a card hold the server refused
 *        with AUTH_REQUIRED, in the page (a Stripe frame, so it also works
 *        inside the Mac app).
 *
 * @details
 * BACKEND 00145 (17-10-2026, payments fix 3): a hold that needs 3DS is no
 * longer treated as good. The refusal carries `clientSecret` and
 * `paymentIntentId`; after the challenge the app calls the same endpoint again
 * naming that PaymentIntent (`paymentIntentId` on /billing/preauthorize,
 * `payment_intent_id` on /s2s/start) and the server adopts the hold only if it
 * is now `requires_capture`, ours, and unused.
 *
 * @author DES
 * @date 18-10-2026
 */

import { getStripe } from "./stripeClient";
export { authRequiredDetails } from "./authRequired.ts";

export type BankConfirmation =
  | { ok: true; paymentIntentId: string }
  | { ok: false; code: string; message: string };

/**
 * One bank check at a time (21-10-2026): Stripe refuses a second
 * handleNextAction while one is open ("You have an in-flight
 * handleNextAction!"). The same payment asked twice shares the first check;
 * a different one while a check is open is refused in our own words.
 */
let inFlight: { clientSecret: string; result: Promise<BankConfirmation> } | null = null;

export function confirmWithBank(clientSecret: string, paymentIntentId: string): Promise<BankConfirmation> {
  if (inFlight) {
    if (inFlight.clientSecret === clientSecret) return inFlight.result;
    return Promise.resolve({
      ok: false,
      code: "BANK_CHECK_OPEN",
      message: "Your bank is already checking a payment. Finish that one first.",
    });
  }
  const result = runBankCheck(clientSecret, paymentIntentId).finally(() => {
    inFlight = null;
  });
  inFlight = { clientSecret, result };
  return result;
}

async function runBankCheck(clientSecret: string, paymentIntentId: string): Promise<BankConfirmation> {
  const stripe = await getStripe();
  if (!stripe) {
    return { ok: false, code: "AUTH_REQUIRED", message: "Card payments aren't set up on this site." };
  }
  const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret });
  if (error) {
    return {
      ok: false,
      code: error.decline_code || error.code || "AUTH_REQUIRED",
      message: error.message ?? "Your bank didn't confirm the payment.",
    };
  }
  // A hold ends in requires_capture; a plan's first invoice in succeeded, or
  // processing for a moment (21-10-2026); the server's sync says which.
  if (
    paymentIntent &&
    paymentIntent.status !== "requires_capture" &&
    paymentIntent.status !== "succeeded" &&
    paymentIntent.status !== "processing"
  ) {
    return { ok: false, code: "AUTH_REQUIRED", message: "Your bank didn't confirm the payment." };
  }
  return { ok: true, paymentIntentId: paymentIntent?.id ?? paymentIntentId };
}
